var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Timeouts } from './framework/Timeouts';
const remoteControlCodes = {
    back: '45',
    channelDown: '35',
    channelUp: '34',
    directionDown: '42',
    directionEnter: '40',
    directionLeft: '43',
    directionRight: '44',
    directionUp: '41',
    exit: '46',
    key0: '00',
    key1: '01',
    key2: '02',
    key3: '03',
    key4: '04',
    key5: '05',
    key6: '06',
    key7: '07',
    key8: '08',
    key9: '09',
    keyDash: '10',
    keyEnter: '40',
    menu: '38',
};
var ModelNumbers;
(function (ModelNumbers) {
    ModelNumbers["LC32LE653U"] = "-K_kpw-VUYU9MTpFTPto";
    ModelNumbers["PNLE601"] = "-LbTr11U3mdW9MbP3rsH";
    ModelNumbers["PNLE701"] = "-LbTr11U3mdW9MbP3rsI";
    ModelNumbers["PNLE801"] = "-LbTr11U3mdW9MbP3rsJ";
    ModelNumbers["PNLE901"] = "-LbTr11U3mdW9MbP3rsK";
})(ModelNumbers || (ModelNumbers = {}));
export class SharpAquos {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.defaultModelNumber = ModelNumbers.LC32LE653U;
        this.defaultSuspendPollingMs = 6000;
        this.isMacAddressSpecified = false;
        this.modelNumber = this.defaultModelNumber;
        this.modelInfo = {
            [ModelNumbers.PNLE601]: { suspendPollingMs: 12000 },
            [ModelNumbers.PNLE701]: { suspendPollingMs: 12000 },
            [ModelNumbers.PNLE801]: { suspendPollingMs: 12000 },
            [ModelNumbers.PNLE901]: { suspendPollingMs: 12000 },
        };
        this.suspendPollingMs = this.defaultSuspendPollingMs;
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.channelKeyboardType = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () { return 'numericWithDash'; }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const match = yield this.socket.send(`SWVN1   `, /(\d.+)|ERR/i, tags);
                return (_a = match[1]) !== null && _a !== void 0 ? _a : 'unknown';
            }),
            asyncHandlers: {
                regExp: /SW\s+([\d\.a-z]+)/i,
                callback: (match) => {
                    this.state.firmwareVersion.update(match[1]);
                },
            },
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                const tags = ['firmwareVersion.hasControl', 'initialize'];
                const match = yield this.socket.send(`SWVN1   `, /(\d.+)|ERR/i, tags);
                return match[1] !== undefined;
            }),
            powerOffRestrictions: ['get', 'hasControl'],
        };
        this.mediaOperation = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                if (value.operation === 'channelSet') {
                    // we support both a dot and a dash for channel separation. The Sharp IR remote
                    // uses a dot.
                    const channelHasDash = value.channel.indexOf('-') > -1;
                    const channelHasDot = value.channel.indexOf('.') > -1;
                    this.state.busy.update(true);
                    this.busyTimeout.set(() => this.state.busy.update(false), 5000);
                    if (!(channelHasDash || channelHasDot)) {
                        if (value.channel.length <= 3) {
                            // <= 3 digits with no dash: use analog tuner command
                            const ch = value.channel.padStart(3, '0').padEnd(4, ' ');
                            yield this.socket.send(`DCCH${ch}`, /OK|ERR/i, tags);
                        }
                        else if (value.channel.length === 4) {
                            // 4 digits with no dash: use DA2P direct channel command
                            yield this.socket.send(`DA2P${value.channel}`, /OK|ERR/i, tags);
                        }
                        else {
                            this.log.error(`unhandled channel format: ${value.channel}`);
                        }
                    }
                    else {
                        // If the channel has a dash or a dot, then determine which command to use based
                        // on the channel lengths.
                        const [chU, chL] = value.channel.split(channelHasDash ? '-' : '.');
                        const length = Math.max(2, Math.max(chU.length, chL.length));
                        const dChanU = chU.padStart(length, '0');
                        const dChanL = chL.padStart(length, '0');
                        if (chU && chL) {
                            if (length === 2) {
                                yield this.socket.send(`DA2P${dChanU}${dChanL}`, /OK|ERR/i, tags);
                            }
                            else if (length === 3) {
                                yield this.socket.send(`DC2U${dChanU} `, /OK|ERR/i, tags);
                                yield this.socket.send(`DC2L${dChanL} `, /OK|ERR/i, tags);
                            }
                            else if (length === 4) {
                                yield this.socket.send(`DC10${dChanU}`, /OK|ERR/i, tags);
                                yield this.socket.send(`DC11${dChanL}`, /OK|ERR/i, tags);
                            }
                            else {
                                this.log.error(`unhandled channel format: ${value.channel}`);
                            }
                        }
                    }
                }
                else if (remoteControlCodes.hasOwnProperty(value.operation)) {
                    const operation = (_b = remoteControlCodes[value.operation]) !== null && _b !== void 0 ? _b : '';
                    const key = operation.padEnd(4, ' ');
                    yield this.socket.send(`RCKY${key}`, /OK|ERR/i, tags);
                }
                else {
                    this.log.error(`unhandled media operation: ${value.operation}`);
                }
                return value;
            }),
            powerOffRestrictions: ['set'],
        };
        this.mediaOperations = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _c, _d;
                const mediaOperationCaps = {};
                const mediaOperations = [
                    'back',
                    'channelSet',
                    'channelUpDown',
                    'directionUpDownLeftRight',
                    'directionEnter',
                    'exit',
                    'keyDash',
                    'keyDigits',
                    'keyEnter',
                    'menu',
                ];
                for (const input of Object.values((_d = (_c = this.config) === null || _c === void 0 ? void 0 : _c.videoInputs) !== null && _d !== void 0 ? _d : {})) {
                    const tvTunerCode = '0';
                    if (input.code === tvTunerCode) {
                        mediaOperationCaps[input.code] = [...mediaOperations];
                    }
                }
                return mediaOperationCaps;
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`POWR????`, /(\d+)|OK|ERR/i, tags);
                if (match[1])
                    return match[1] === '1' ? true : false;
                return this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                // Clear any pending busy timeouts that would set busy back to false. The power set
                // methods will set busy to true and manage the transition back to false.
                this.busyTimeout.clear();
                if (this.isMacAddressSpecified) {
                    if (this.socket.options.allowNoConnection) {
                        return this.setPowerWakeOnLanNetwork(power, tags);
                    }
                    else {
                        return this.setPowerWakeOnLanSerial(power, tags);
                    }
                }
                return this.setPowerSimple(power, tags);
            }),
            powerOffRestrictions: (config) => {
                return config.macAddress === undefined ? [] : ['get'];
            },
        };
        this.serialNumber = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _e;
                const match = yield this.socket.send(`SRNO????`, /ERR|(.+)/i, tags);
                return (_e = match[1]) !== null && _e !== void 0 ? _e : 'unknown';
            }),
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                const tags = ['serialNumber.hasControl', 'initialize'];
                const match = yield this.socket.send(`SRNO????`, /ERR|(.+)/i, tags);
                return match[1] !== undefined;
            }),
            powerOffRestrictions: ['get', 'hasControl'],
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`IAVD????`, /(\d+)|OK|ERR|WAIT/i, tags);
                if (match[1])
                    return match[1];
                return this.state.videoInput.value;
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`IAVD${code.padEnd(4, ' ')}`, /(OK)|ERR|WAIT/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                if (match[1])
                    return code;
                return this.state.videoInput.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            range: { max: 100, min: 0, round: 1 },
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`VOLM????`, /(\d+)|OK|WAIT|ERR/i, tags);
                if (match[1])
                    return parseInt(match[1], 10);
                return this.state.volume.value;
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`VOLM${volume.toString().padStart(3, '0')} `, /(\d+|OK|WAIT)|ERR/i, tags);
                if (match[1])
                    return volume;
                return this.state.volume.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`MUTE????`, /(\d+)|OK|WAIT|ERR/i, tags);
                if (match[1])
                    return match[1] === '1' ? true : false;
                return this.state.volumeMute.value;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`MUTE${mute ? '1   ' : '2   '}`, /(OK)|ERR|WAIT/, tags);
                return match[1] ? mute : this.state.volumeMute.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            { intervalMs: 3000, controls: ['power', 'videoInput'] },
            { intervalMs: 5000, controls: ['volume', 'volumeMute'] },
            {
                intervalMs: 5000,
                pollWhenDisconnected: true,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (!this.state.power.value &&
                        this.socket.options.allowNoConnection &&
                        this.socket.getState() !== 'connected') {
                        if (!this.framework.isReconnecting)
                            yield this.framework.reconnect();
                    }
                }),
            },
        ];
        if (this.socket.options.allowNoConnection) {
            // If the display is powered off externally (e.g., IR remote or third party
            // controller), then we will lose the socket connection. If this happens, we update
            // the power state to false.
            this.socket.onConnectionLost(() => {
                this.state.power.update(false, true);
            });
        }
    }
    // phase callbacks
    onConfigure(config) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.config = config;
            this.modelNumber = (_a = config.modelNumber) !== null && _a !== void 0 ? _a : this.defaultModelNumber;
            this.suspendPollingMs =
                (_c = (_b = this.modelInfo[this.modelNumber]) === null || _b === void 0 ? void 0 : _b.suspendPollingMs) !== null && _c !== void 0 ? _c : this.defaultSuspendPollingMs;
            this.isMacAddressSpecified = config.macAddress !== undefined;
            if (this.isMacAddressSpecified && config.port === undefined) {
                this.log.error(`mac address configured for wake on lan, but no port specified`);
            }
            if (config.macAddress !== undefined) {
                // The mac address is specified, so we can't query the power if the device is powered
                // off. The power is initialized to false, but we need to query it if we can when we
                // first connect.
                if (this.socket.port !== 10002 || this.socket.state === 'connected') {
                    this.state.power.update(yield this.power.get(['power.get']));
                }
            }
            else {
                // Power on command setting: for RS232, set to 1; for IP, set to 2. (SNTY-2970)
                yield this.socket.sendAsync(`RSPW${this.socket.port === 10002 ? '   2' : '   1'}`, [
                    'power.on.enable',
                ]);
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    setPowerSimple(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(this.suspendPollingMs);
            this.socket.removeCommands('poll');
            const match = yield this.socket.send(`POWR${power ? '1   ' : '0   '}`, /(OK|\d+)|WAIT|ERR/i, tags);
            this.state.busy.update(true);
            if (match[1]) {
                const newPower = match[1] === 'OK' ? power : this.state.power.value;
                this.state.power.update(newPower);
                yield this.timeouts.delay(newPower ? this.suspendPollingMs : 2000);
            }
            this.state.busy.update(false);
            return this.state.power.value;
        });
    }
    setPowerWakeOnLanNetwork(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(this.suspendPollingMs);
            this.socket.removeCommands('poll');
            if (power) {
                // For direct Ethernet connections where the socket connection is lost
                // when the display is powered off, we first uppdate the power state so the
                // UI will reflect that the power is on, then we block while sending a wake
                // on lan request and attempting to reconnect to the display.
                this.state.busy.update(true);
                this.state.power.update(power);
                yield this.framework.reconnect({
                    attemptDelayMs: 1000,
                    initialDelayMs: 0,
                    numAttempts: 8,
                    onBeforeReconnect: () => __awaiter(this, void 0, void 0, function* () {
                        return this.framework.sendWakeOnLan();
                    }),
                });
                // When updating the busy state, we must use notifyIfUnchanged = true. This
                // is because the driver reconnects as part of powering on, and
                // reconnecting resets all the states. Without notifyIfUnchanged = true,
                // the busy = false notification won't get sent.
                this.state.busy.update(false, true);
                return this.socket.state === 'connected';
            }
            else {
                const match = yield this.socket.send(`POWR${power ? '1   ' : '0   '}`, /(OK|\d+)|WAIT|ERR/i, tags);
                this.state.busy.update(true);
                if (match[1]) {
                    const newPower = match[1] === 'OK' ? power : this.state.power.value;
                    this.state.power.update(newPower);
                    yield this.timeouts.delay(this.suspendPollingMs);
                }
                this.state.busy.update(false);
                return this.state.power.value;
            }
        });
    }
    setPowerWakeOnLanSerial(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(this.suspendPollingMs);
            this.socket.removeCommands('poll');
            if (power) {
                // For serial connections where the display is unresponsive when powered
                // off, first we update the power state so the UI will reflect that the
                // power is on, then we block while sending a wake on lan request, then we
                // continue blocking while sending some power requests to confirm that the
                // power is on, finally we return the new power state.
                this.state.busy.update(true);
                this.state.power.update(true);
                let newPower = false;
                for (let i = 0; i < 4; i++) {
                    this.framework.suspendPolling(this.suspendPollingMs);
                    yield this.framework.sendWakeOnLan();
                    try {
                        const match = yield this.socket.send(`POWR????`, /(\d+)|OK|ERR/i, [
                            'power.get',
                        ]);
                        newPower = match[1] === '1';
                        break;
                    }
                    catch (_a) { }
                }
                this.state.busy.update(false);
                return newPower;
            }
            else {
                return this.setPowerSimple(power, tags);
            }
        });
    }
}
SharpAquos.driverType = 'socketDriver';
SharpAquos.key = '-LbTfcnZbcMdADSq8QWi';
SharpAquos.socketOptions = (config) => {
    const baseSocketOptions = {
        commandExtractor: {
            terminators: ['\r', '\n'],
        },
        sendTerminator: '\r',
        idleTimeoutMs: 27000,
        sendTimeoutMs: 9000,
    };
    const hasMacAddress = config.macAddress !== undefined;
    if (config.port === undefined) {
        return [
            Object.assign(Object.assign({}, baseSocketOptions), { port: 10002, allowNoConnection: hasMacAddress }),
            Object.assign(Object.assign({}, baseSocketOptions), { port: 4999, idleTimeoutMs: hasMacAddress ? 0 : baseSocketOptions.idleTimeoutMs, allowNoConnection: false }),
        ];
    }
    // Assume port 10002 is direct Ethernet to the device and anything else is via an Ethernet
    // to serial converter.
    return [
        Object.assign(Object.assign({}, baseSocketOptions), { port: config.port, idleTimeoutMs: hasMacAddress && config.port !== 10002 ? 0 : baseSocketOptions.idleTimeoutMs, allowNoConnection: hasMacAddress && config.port === 10002 }),
    ];
};
